import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Flex, Icon, Text, Tooltip } from '~/components/blocks';
import { ActionButton } from '~/components/partials/ReceptionDrawer/ActionButton';
import { PatientProfileFooterAppointmentFragment } from '~/graphql/generated/graphql';
import { useUberOrganizationSetting } from '~/hooks/use-uber_organization_setting';
import { changeDeliveryMethodState } from '~/state/partials/change_delivery_method_modal/atoms';
import { chargeModalState } from '~/state/partials/charge_modal/atoms';
import { requestUberDeliveryModalState } from '~/state/reception/atoms';
import { RequestUberDeliveryModalState } from '~/state/reception/types';

import { useFinishAppointment } from './use-finish-appointment';

type Props = {
  appointment: PatientProfileFooterAppointmentFragment;
};

const RequestButton = styled(ActionButton)(() =>
  css({
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
      pointerEvents: 'none',
      '&:hover': {
        boxShadow: 'none',
      },
    },
  }),
);

const DisabledButton = styled(ActionButton)(() =>
  css({
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
      pointerEvents: 'inherit',
    },
  }),
);

export const SameDayDeliveryButtons = React.memo((props: Props) => {
  const { appointment } = props;
  const uberDelivery = appointment.uberDelivery;
  const { finishing, handleFinish } = useFinishAppointment(appointment.id);
  const { loading, enable: enabledUber } = useUberOrganizationSetting();
  const setState = useSetAtom(requestUberDeliveryModalState);

  const handleRequestDelivery = useCallback(
    () =>
      setState((_state: RequestUberDeliveryModalState) => ({
        ..._state,
        isOpen: true,
        appointmentId: appointment.id,
      })),
    [setState, appointment],
  );

  const setChangeDeliveryMethodModal = useSetAtom(changeDeliveryMethodState);
  const handleChangeDeliveryMethod = useCallback(() => {
    setChangeDeliveryMethodModal({
      isOpen: true,
      appointmentId: appointment.id,
    });
  }, [appointment.id, setChangeDeliveryMethodModal]);

  const setChargeModalState = useSetAtom(chargeModalState);
  // - 未会計 -> 会計済(配送待ち)
  const handleOpenChargeModal = useCallback(() => {
    setChargeModalState({
      appointmentId: appointment.id,
      isOpen: true,
    });
  }, [appointment.id, setChargeModalState]);

  if (loading) {
    return null;
  }

  if (!enabledUber && !uberDelivery) {
    return (
      <ActionButton use="primaryInverse" wide="fill" size="l" onClick={handleChangeDeliveryMethod}>
        受け渡し方法を変更
      </ActionButton>
    );
  }

  if (!uberDelivery) {
    return (
      <RequestButton use="primary" wide="fill" size="l" onClick={handleRequestDelivery}>
        集荷依頼に進む
      </RequestButton>
    );
  }

  if (uberDelivery.status === 'uber_support_canceled') {
    return (
      <Tooltip
        content={
          <Flex alignItems="center" justifyContent="center" color="white">
            <Icon icon="attention" size="m" />
            <Text color="white">状況確認中です。完了するまで操作できません</Text>
          </Flex>
        }
      >
        <DisabledButton use="primary" wide="fill" size="l" disabled>
          {appointment.status === 'charged' ? '対応完了' : '会計'}
        </DisabledButton>
      </Tooltip>
    );
  }

  if (uberDelivery.status === 'before_dropoff_canceled') {
    return (
      <RequestButton use="primary" wide="fill" size="l" onClick={handleRequestDelivery}>
        集荷依頼に進む
      </RequestButton>
    );
  }

  if (uberDelivery.status === 'unfulfilled') {
    return (
      <RequestButton use="primary" wide="fill" size="l" onClick={handleRequestDelivery}>
        集荷依頼に進む
      </RequestButton>
    );
  }

  if (uberDelivery.status === 'pending' && uberDelivery.isTimeElapsedNotification) {
    return (
      <ActionButton use="primaryInverse" wide="fill" size="l" onClick={handleChangeDeliveryMethod}>
        受け渡し方法を変更
      </ActionButton>
    );
  }

  if (uberDelivery.status === 'pending' && !uberDelivery.isTimeElapsedNotification) {
    return (
      <RequestButton use="primary" wide="fill" size="l" disabled>
        集荷依頼に進む
      </RequestButton>
    );
  }

  if (appointment.status === 'waiting_for_charge') {
    return (
      <ActionButton use="primary" wide="fill" size="l" onClick={handleOpenChargeModal}>
        会計
      </ActionButton>
    );
  }

  if (uberDelivery.status === 'canceled' || uberDelivery.status === 'return_delivered') {
    return (
      <ActionButton use="primary" wide="fill" size="l" loading={finishing} onClick={handleFinish}>
        対応完了
      </ActionButton>
    );
  }
});

SameDayDeliveryButtons.displayName = 'SameDayDeliveryButtons';
